import {
    Box,
    Button,
    HStack,
    ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    StackDivider,
    Text,
    UnorderedList,
    VStack,
} from '@chakra-ui/react';
import React from 'react';
import type {
    PublicChangelogConfig,
    PublicChangelogRelease,
} from './public-changelog-config';
import { usePublicChangelog } from './use-public-changelog';

type PublicChangelogModalProps = {
    config: PublicChangelogConfig;
};

export const PublicChangelogModal: React.FC<PublicChangelogModalProps> = ({
    config,
}) => {
    const { hasNewReleases, markAsSeen, releases } = usePublicChangelog(config);

    if (releases.length === 0) {
        return null;
    }

    return (
        <Modal
            isOpen={hasNewReleases}
            onClose={markAsSeen}
            isCentered
            size="xl"
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>What's New</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack
                        spacing={6}
                        divider={
                            <StackDivider
                                borderColor="gray.500"
                                opacity="0.5"
                            />
                        }
                        w="100%"
                        alignItems="start"
                    >
                        {releases.map((release) => (
                            <Release key={release.version} release={release} />
                        ))}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={markAsSeen}>Got it!</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

const Release = ({
    release: { version, changes, date },
}: {
    release: PublicChangelogRelease;
}) => (
    <Box>
        <HStack spacing={2} alignItems="baseline">
            <Text fontWeight="bold" fontSize="lg" mb={2}>
                Version {version}
            </Text>
            <Text fontSize="sm" color="gray.500">
                {date}
            </Text>
        </HStack>

        <ChangesSection title="Added" changes={changes.added} />

        <ChangesSection title="Changed" changes={changes.changed} />

        <ChangesSection title="Fixed" changes={changes.fixed} />

        <ChangesSection title="Deprecated" changes={changes.deprecated} />

        <ChangesSection title="Removed" changes={changes.removed} />
    </Box>
);

const ChangesSection = ({
    title,
    changes,
}: {
    title: string;
    changes?: string[];
}) => {
    if (!changes || changes.length === 0) return null;

    return (
        <Box mb={4}>
            <Text fontWeight="bold" mb={2} opacity={0.75}>
                {title}:
            </Text>
            <UnorderedList spacing={2}>
                {changes.map((change, index) => (
                    <ListItem key={index}>{change}</ListItem>
                ))}
            </UnorderedList>
        </Box>
    );
};
